import { Body, Col, IconButton, Row, Stack, SubHeading } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { DateTimeText } from '~/features/temporal';
import { I18n } from '~/common/explicit-exports';
import StringUtils from '~/common/string-utils';
import { StyledCard } from './WebhookSubscriptionCard.styles';

interface WebhookSubscriptionCardProps {
  name: string;
  url: string;
  eventTypes: string[];
  updatedAt: number;
  lastUpdatedBy?: { name?: string; email: string };
  onEdit?: () => void;
  onDelete?: () => void;
  onTest?: () => void;
}

export const WebhookSubscriptionCard = ({
  name,
  url,
  eventTypes,
  updatedAt,
  lastUpdatedBy,
  onEdit,
  onDelete,
  onTest,
}: WebhookSubscriptionCardProps) => {
  const theme = useTheme();

  const EDIT_WEBHOOK = I18n.t('js.admin.integration_settings.webhook_subscriptions.edit_webhook');
  const DELETE_WEBHOOK = I18n.t('js.admin.integration_settings.webhook_subscriptions.delete_webhook');
  const TEST_WEBHOOK = I18n.t('js.admin.integration_settings.webhook_subscriptions.test_webhook');
  const UPDATED = I18n.t('js.admin.integration_settings.webhook_subscriptions.card.updated');
  const SUBSCRIPTION_EVENTS = I18n.t('js.admin.integration_settings.webhook_subscriptions.card.subscription_events');
  const UPDATED_BY = lastUpdatedBy
    ? I18n.t('js.admin.integration_settings.webhook_subscriptions.card.updated_by', {
        name: lastUpdatedBy?.name || lastUpdatedBy?.email,
      })
    : '';

  return (
    <StyledCard>
      <Row gapX={4} align="flex-start" justify="space-between">
        <Col>
          <Stack size={2}>
            <SubHeading level="md">{name}</SubHeading>
            <Body level="md" color={theme.colors.on[600]}>
              {url}
            </Body>
            <Body level="md" color={theme.colors.on[600]}>
              {SUBSCRIPTION_EVENTS}:{' '}
              {eventTypes
                .map((type) => StringUtils.getCapitalizedWordsFromCamelCasedString(type.replace('.', ': ')))
                .join(', ')}
            </Body>
            <Body level="md" color={theme.colors.on[600]}>
              {UPDATED}: <DateTimeText isLocalTime showWeekday dateStyle="long" date={updatedAt} /> {UPDATED_BY}
            </Body>
          </Stack>
        </Col>
        {onEdit && onDelete && (
          <Col xs="auto">
            <Row gapX={2}>
              <Col xs="auto">
                <IconButton
                  iconName="send:regular"
                  onPress={onTest}
                  label={TEST_WEBHOOK}
                  variant="outline"
                  color="secondary"
                />
              </Col>
              <Col xs="auto">
                <IconButton
                  iconName="edit:regular"
                  onPress={onEdit}
                  label={EDIT_WEBHOOK}
                  variant="outline"
                  color="secondary"
                />
              </Col>
              <Col xs="auto">
                <IconButton
                  iconName="trash:regular"
                  onPress={onDelete}
                  label={DELETE_WEBHOOK}
                  variant="outline"
                  color="danger"
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </StyledCard>
  );
};
